import React, { useMemo } from 'react';

import { ReactComponent as RefreshIcon } from 'assets/images/icons/union.svg';

import { Select } from 'ui/Select';

import { GATEWAY_MODELS, GatewayFilterObject, GatewayFilterUser } from 'services/gateways';
import { FiltersState } from 'views/Gateways/Gateways';

import styles from 'assets/scss/modules/Gateways/Gateways.module.scss';

import { FilterSelect, FilterSelectOption } from './FilterSelect';

interface Props {
  values: FiltersState;
  users: GatewayFilterUser[];
  objects: GatewayFilterObject[];
  onChange: (next: FiltersState) => void;
  onClear: () => void;
}

// FR-85 п.2: список статусов всегда одинаковый, вне зависимости от того,
// какие статусы реально присутствуют в списке шлюзов.
const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'online', label: 'Online' },
  { value: 'offline', label: 'Offline' },
  { value: 'never_seen', label: 'Never seen' },
];

// FR-86 п.2/3: список моделей всегда одинаковый и в фиксированном порядке,
// вне зависимости от того, какие модели реально присутствуют в списке шлюзов.
const MODEL_OPTIONS = [
  { value: '', label: 'All models' },
  ...GATEWAY_MODELS.map((m) => ({ value: m, label: m })),
];

export const Filters: React.FC<Props> = ({
  values,
  users,
  objects,
  onChange,
  onClear,
}) => {
  const userOptions = useMemo<FilterSelectOption[]>(
    () => users.map((u) => ({ value: String(u.id), label: u.fullname })),
    [users],
  );

  // FR-84 3b: the object list depends on the selected user.
  // No user -> all objects; user selected -> only that user's objects.
  const objectOptions = useMemo<FilterSelectOption[]>(
    () =>
      objects
        .filter((o) => !values.user || String(o.user_id) === values.user)
        .map((o) => ({ value: String(o.id), label: o.name })),
    [objects, values.user],
  );

  // FR-84 3b.iv: changing the user resets the object back to "All objects".
  const handleUserChange = (user: string) => {
    onChange({ ...values, user, object: '' });
  };

  // FR-84 3b.iii: picking an object while no user is set auto-fills the user
  // with the object's owner.
  const handleObjectChange = (object: string) => {
    if (!object) {
      onChange({ ...values, object: '' });
      return;
    }
    let user = values.user;
    if (!user) {
      const owner = objects.find((o) => String(o.id) === object);
      if (owner) user = String(owner.user_id);
    }
    onChange({ ...values, object, user });
  };

  return (
    <div className={styles.filtersRow}>
      <FilterSelect
        value={values.user}
        allLabel="All users"
        options={userOptions}
        onChange={handleUserChange}
      />
      <FilterSelect
        value={values.object}
        allLabel="All objects"
        options={objectOptions}
        onChange={handleObjectChange}
      />
      <Select
        name="status"
        value={values.status}
        options={STATUS_OPTIONS}
        placeholder="All statuses"
        wrapperClassName={styles.filterSelect}
        onChange={(v) => onChange({ ...values, status: v })}
      />
      <Select
        name="model"
        value={values.model}
        options={MODEL_OPTIONS}
        placeholder="All models"
        wrapperClassName={styles.filterSelect}
        onChange={(v) => onChange({ ...values, model: v })}
      />

      <button
        type="button"
        className={styles.filterClearButton}
        onClick={onClear}
        aria-label="Clear filters"
      >
        <RefreshIcon />
      </button>

      <button
        type="button"
        className={styles.filterClearText}
        onClick={onClear}
      >
        Clear filters
      </button>
    </div>
  );
};
